import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '../../ui/core';
import api from '../../lib/api';
import { REASON_MAX, validateReason } from './sdpFormatters';

// Copy for each kind of clear. The safe-mode copy matters most: clearing a
// safe city writes a manual sdp_policy_change, and the nightly job treats
// every manual row as a hard reset.
const SAFE_COPY = (name) =>
  `Clearing safe mode for ${name} writes a manual policy change. The nightly job treats EVERY manual row as a hard reset, ` +
  `so ${name}'s refit window restarts and the next reference carries a reset reason, which also clears safe mode ` +
  `anywhere it's still open. If D1 or D3 still breach, safe mode re-opens on the next nightly guard input.`;

// A cleared guard re-opens only when it is evaluated again: liveness (L*) and
// intraday (E1) guards every tick, daily guards (D1–D6) on the next nightly
// GUARDRAIL_INPUT run (SdpGuardService never re-opens from the run it judged).
const AUTO_CLEAR_COPY =
  'explore_off and frozen clear on their own (except I7, a refusal-burst freeze, which waits for a human): ' +
  'explore_off at the next passing evaluation; frozen when the condition clears, or at city-local midnight for E1. ' +
  'Clearing early writes no policy change. If the condition persists the guard re-opens: liveness and intraday ' +
  'guards on the next tick, daily guards (D1–D6) on the next nightly guard input.';

const LANE_COPY = (name) =>
  `Clears rider safe mode (D6) on ${name}. Lane clears write no policy change. If the lane still breaches, D6 ` +
  `re-opens on the next nightly guard input.`;

const copyFor = (target) => {
  if (target.scope === 'lane') return LANE_COPY(target.name);
  if (target.guardState === 'safe') return SAFE_COPY(target.name);
  return AUTO_CLEAR_COPY;
};

const confirmLabelFor = (target) => (target.scope === 'lane' ? 'Clear rider safe mode' : `Clear ${target.guardState}`);

/**
 * target: { scope: 'city'|'lane', scopeId, name, guardState } | null
 * onCleared(result) runs after a successful POST (the caller refetches);
 * the dialog stays open showing the result until closed.
 */
const ClearGuardDialog = ({ target, onClose, onCleared }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [serverError, setServerError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  // Which opening of the dialog a response belongs to: a late response for
  // an earlier target must never show as this target's result.
  const opening = useRef(0);

  useEffect(() => {
    opening.current += 1;
    setReason('');
    setError(null);
    setServerError(null);
    setResult(null);
    setSubmitting(false);
  }, [target]);

  if (!target) return null;

  const onConfirm = async () => {
    const reasonError = validateReason(reason);
    setError(reasonError);
    if (reasonError) return;
    const sentFor = opening.current;
    setSubmitting(true);
    setServerError(null);
    try {
      const response = await api.supplyDemandPace.clearGuard({
        scope: target.scope,
        scopeId: target.scopeId,
        reason: reason.trim(),
      });
      // The clear happened either way: let the caller refresh.
      onCleared && onCleared(response);
      if (sentFor === opening.current) setResult(response);
    } catch (e) {
      if (sentFor === opening.current) setServerError(e.message);
    } finally {
      if (sentFor === opening.current) setSubmitting(false);
    }
  };

  const isSafeCity = target.scope === 'city' && target.guardState === 'safe';

  return (
    <Dialog open={!!target} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {target.scope === 'lane' ? `Clear rider safe mode: ${target.name}` : `Clear ${target.guardState}: ${target.name}`}
      </DialogTitle>
      <DialogContent>
        {result ? (
          <Stack spacing="8px">
            <Alert severity="success">Cleared.</Alert>
            <Typography sx={{ fontSize: '14px' }}>
              {`guardState: ${result.guardState} (was ${result.previousState})`}
            </Typography>
            <Typography sx={{ fontSize: '14px' }}>{`Rows updated: ${result.rowsUpdated}`}</Typography>
            <Typography sx={{ fontSize: '14px' }}>
              {`Closed events: ${(result.closedEventIds || []).length}`}
            </Typography>
            <Typography sx={{ fontSize: '14px' }}>
              {result.policyChange
                ? `Manual policy change written (${result.policyChange.id}); the nightly job will hard-reset.`
                : 'No policy change written.'}
            </Typography>
            {result.guardState !== 'normal' && (
              <Alert severity="warning">
                {`Still ${result.guardState}: another open event (e.g. a global guard) keeps this city non-normal.`}
              </Alert>
            )}
          </Stack>
        ) : (
          <Stack spacing="12px">
            <Alert severity={isSafeCity ? 'warning' : 'info'}>{copyFor(target)}</Alert>
            <TextField
              name="reason"
              label="Reason (required)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={!!error}
              helperText={error || `${reason.trim().length}/${REASON_MAX}`}
              multiline
              minRows={2}
              fullWidth
            />
            {serverError && <Alert severity="error">{serverError}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px' }}>
        {result ? (
          <Button variant="contained" onClick={onClose} disableElevation>
            Close
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Button variant="outlined" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={onConfirm} disabled={submitting} disableElevation>
              {confirmLabelFor(target)}
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ClearGuardDialog;
